/**
 * Default rail widths for the Image Lab shell. Bumped 50% on 2026-05-17
 * (240 → 360 left, 340 → 510 right) because too many widgets (width / height /
 * numImages / seed + the Operations/Agents tabs + the provider+model picker)
 * were getting crunched at the prior defaults.
 *
 * F-panel-resize will lift these into state with localStorage persistence;
 * keeping the constants in one place so that slice is a single import-swap.
 */

export const DEFAULT_LEFT_RAIL_WIDTH = 360;
export const DEFAULT_RIGHT_RAIL_WIDTH = 510;

/** Bounds enforced by the resize splitter. The canvas needs room to render at
 *  any sensible size, and the right rail's chip-row labels overflow below
 *  about 280px. */
export const LEFT_RAIL_MIN = 220;
export const LEFT_RAIL_MAX = 600;
export const RIGHT_RAIL_MIN = 320;
export const RIGHT_RAIL_MAX = 720;

/** Clamp a proposed width into the panel's allowed range. */
export function clampRailWidth(
  proposed: number,
  side: 'left' | 'right',
): number {
  const min = side === 'left' ? LEFT_RAIL_MIN : RIGHT_RAIL_MIN;
  const max = side === 'left' ? LEFT_RAIL_MAX : RIGHT_RAIL_MAX;
  if (!Number.isFinite(proposed)) return side === 'left' ? DEFAULT_LEFT_RAIL_WIDTH : DEFAULT_RIGHT_RAIL_WIDTH;
  return Math.min(max, Math.max(min, Math.round(proposed)));
}

const LEFT_KEY = 'ct_v3_left_rail_width';
const RIGHT_KEY = 'ct_v3_right_rail_width';

function storageKey(side: 'left' | 'right'): string {
  return side === 'left' ? LEFT_KEY : RIGHT_KEY;
}

/** Read a persisted rail width; falls back to the default when missing,
 *  malformed, or out of range. Never throws. */
export function loadRailWidth(side: 'left' | 'right', storage?: Storage | null): number {
  const fallback = side === 'left' ? DEFAULT_LEFT_RAIL_WIDTH : DEFAULT_RIGHT_RAIL_WIDTH;
  const store = storage ?? (typeof window === 'undefined' ? null : window.localStorage);
  if (!store) return fallback;
  let raw: string | null;
  try {
    raw = store.getItem(storageKey(side));
  } catch {
    return fallback;
  }
  if (!raw) return fallback;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return fallback;
  return clampRailWidth(parsed, side);
}

/** Persist a rail width. Silently swallows quota / private-mode errors. */
export function saveRailWidth(side: 'left' | 'right', width: number, storage?: Storage | null): void {
  const store = storage ?? (typeof window === 'undefined' ? null : window.localStorage);
  if (!store) return;
  try {
    store.setItem(storageKey(side), String(clampRailWidth(width, side)));
  } catch {
    // best-effort persistence
  }
}

/**
 * Convert a pointer-drag delta (in screen pixels) to a new rail width.
 * For the left rail a positive delta widens it; for the right rail a
 * positive delta narrows it (the splitter sits on the rail's inside
 * edge so dragging right shrinks the right rail). Result is clamped.
 */
export function widthFromDragDelta(
  startWidth: number,
  deltaPx: number,
  side: 'left' | 'right',
): number {
  const proposed = side === 'left' ? startWidth + deltaPx : startWidth - deltaPx;
  return clampRailWidth(proposed, side);
}
